import {
    faAnglesDown,
    faChevronDown,
    faChevronRight,
    faGripDotsVertical,
    faPlus,
    faTrashCan,
} from '@fortawesome/pro-regular-svg-icons';
import React, { useEffect, useRef, useState } from 'react';
import { DndProvider, useDrag, useDragLayer, useDrop } from 'react-dnd';
import { getEmptyImage, HTML5Backend } from 'react-dnd-html5-backend';
import { useTranslation } from 'react-i18next';

import ConfirmModal from '../../../../core/components/ConfirmModal.tsx';
import TextField from '../../../../core/components/Fields/TextField.tsx';
import { withIcon } from '../../../../core/components/Icon.tsx';
import classnames from '../../../../core/utils/classnames.tsx';

export interface InstructionSection {
    label: string;
    body: string;
}

const DRAG_TYPE = 'INSTRUCTION_SECTION';

const DragIcon = withIcon(faGripDotsVertical);
const ChevronDownIcon = withIcon(faChevronDown);
const ChevronRightIcon = withIcon(faChevronRight);
const AddIcon = withIcon(faPlus);
const DeleteIcon = withIcon(faTrashCan);
const DoubleArrowDownIcon = withIcon(faAnglesDown);

const SectionDragPreview = ({ section, isExpanded }: { section: InstructionSection; isExpanded: boolean }) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    return (
        <div className="border border-line-strong border-solid rounded-xl bg-surface-default overflow-hidden shadow-lg">
            <div className="flex items-center gap-1 px-2 py-2">
                <div className="text-content-weak flex-shrink-0 p-1">
                    <DragIcon className="text-sm" />
                </div>
                <div className="flex-shrink-0 p-0.5 text-content-medium">
                    {isExpanded ? <ChevronDownIcon className="text-xs" /> : <ChevronRightIcon className="text-xs" />}
                </div>
                <span className="flex-1 min-w-0 text-sm font-semibold text-content-strong px-1 truncate">
                    {section.label || t('form.instructionsSectionLabelPlaceholder')}
                </span>
                <div className="text-content-weak flex-shrink-0 p-1">
                    <DeleteIcon className="text-sm" />
                </div>
            </div>
            {isExpanded && (
                <div className="px-3 pb-3">
                    <div className="w-full min-h-[80px] rounded border border-solid border-line-default bg-surface-weak px-3 py-2 text-sm text-content-strong whitespace-pre-wrap break-words">
                        {section.body}
                    </div>
                </div>
            )}
        </div>
    );
};

const SectionDragLayer = ({ sections }: { sections: InstructionSection[] }) => {
    const { isDragging, item, sourceOffset } = useDragLayer((monitor) => ({
        isDragging: monitor.isDragging(),
        item: monitor.getItem(),
        sourceOffset: monitor.getSourceClientOffset(),
    }));

    if (!isDragging || !sourceOffset || !item) return null;

    const section = sections[item.index];
    if (!section) return null;

    return (
        <div className="fixed inset-0 pointer-events-none z-50">
            <div
                style={{
                    position: 'absolute',
                    top: sourceOffset.y,
                    left: sourceOffset.x,
                    width: item.width,
                    opacity: 0.95,
                }}
            >
                <SectionDragPreview section={section} isExpanded={item.isExpanded} />
            </div>
        </div>
    );
};

interface SectionRowProps {
    section: InstructionSection;
    index: number;
    isExpanded: boolean;
    onToggle: () => void;
    onLabelChange: (label: string) => void;
    onBodyChange: (body: string) => void;
    onDeleteRequest: () => void;
    onMove: (from: number, to: number) => void;
}

const SectionRow = ({
    section,
    index,
    isExpanded,
    onToggle,
    onLabelChange,
    onBodyChange,
    onDeleteRequest,
    onMove,
}: SectionRowProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const itemRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<HTMLButtonElement>(null);
    const [isEditingLabel, setIsEditingLabel] = useState(false);

    const [, drop] = useDrop({
        accept: DRAG_TYPE,
        hover(item: any, monitor) {
            if (!itemRef.current) return;
            const dragIndex = item.index;
            const hoverIndex = index;
            if (dragIndex === hoverIndex) return;

            const hoverRect = itemRef.current.getBoundingClientRect();
            const hoverMiddleY = (hoverRect.bottom - hoverRect.top) / 2;
            const clientOffset = monitor.getClientOffset();
            const hoverClientY = clientOffset!.y - hoverRect.top;

            if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) return;
            if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) return;

            onMove(dragIndex, hoverIndex);
            item.index = hoverIndex;
        },
    });

    const [{ isDragging }, drag, preview] = useDrag({
        type: DRAG_TYPE,
        item: () => ({
            index,
            width: itemRef.current?.getBoundingClientRect().width ?? 0,
            isExpanded,
        }),
        collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    });

    useEffect(() => {
        preview(getEmptyImage(), { captureDraggingState: true });
    }, [preview]);

    drag(dragRef);
    drop(itemRef);

    return (
        <div
            ref={itemRef}
            className={classnames(
                'border border-solid rounded-xl bg-surface-default overflow-hidden transition-all',
                isDragging ? 'border-line-weak border-dashed opacity-40' : 'border-line-weak'
            )}
        >
            <div className="flex items-center gap-1 px-2 py-2">
                <button
                    ref={dragRef}
                    type="button"
                    className="text-content-weak hover:text-content-medium cursor-grab active:cursor-grabbing flex-shrink-0 p-1"
                    tabIndex={-1}
                >
                    <DragIcon className="text-sm" />
                </button>
                <button
                    type="button"
                    onClick={onToggle}
                    className="text-content-medium hover:text-content-strong flex-shrink-0 p-1"
                >
                    {isExpanded ? <ChevronDownIcon className="text-xs" /> : <ChevronRightIcon className="text-xs" />}
                </button>
                <input
                    type="text"
                    value={section.label}
                    onChange={(e) => onLabelChange(e.target.value)}
                    onFocus={() => setIsEditingLabel(true)}
                    onBlur={() => setIsEditingLabel(false)}
                    placeholder={t('form.instructionsSectionLabelPlaceholder')}
                    className={classnames(
                        'flex-1 min-w-0 text-sm font-semibold text-content-strong placeholder:text-content-weak transition-colors duration-200 rounded px-2 py-1',
                        isEditingLabel
                            ? 'bg-surface-weak border border-solid border-line-brand-default outline-1 outline outline-line-brand-weak shadow-inner-sm'
                            : 'bg-transparent border border-transparent outline-none'
                    )}
                />
                <button
                    type="button"
                    onClick={onDeleteRequest}
                    className="text-content-weak hover:text-red-500 flex-shrink-0 p-1 transition-colors"
                >
                    <DeleteIcon className="text-sm" />
                </button>
            </div>
            {isExpanded && (
                <div className="px-3 pb-3">
                    <TextField
                        value={section.body}
                        onValueChange={onBodyChange}
                        inputClassName="resize-y min-h-[80px]"
                        placeholder={t('form.instructionsSectionBodyPlaceholder')}
                    />
                </div>
            )}
        </div>
    );
};

interface InstructionSectionsProps {
    label: string;
    helpText?: string;
    value: InstructionSection[];
    onChange: (sections: InstructionSection[]) => void;
}

const InstructionSectionsInner = ({ label, helpText, value, onChange }: InstructionSectionsProps) => {
    const { t } = useTranslation('assistants', { keyPrefix: 'agentConfig' });
    const sections = value || [];
    const [expandedIndexes, setExpandedIndexes] = useState<Set<number>>(new Set([0]));
    const [pendingDeleteIndex, setPendingDeleteIndex] = useState<number | null>(null);

    const allExpanded = sections.length > 0 && expandedIndexes.size === sections.length;

    const toggleExpandAll = () => {
        if (allExpanded) {
            setExpandedIndexes(new Set());
        } else {
            setExpandedIndexes(new Set(sections.map((_, i) => i)));
        }
    };

    const toggleSection = (index: number) => {
        setExpandedIndexes((prev) => {
            const next = new Set(prev);
            if (next.has(index)) {
                next.delete(index);
            } else {
                next.add(index);
            }
            return next;
        });
    };

    const handleMove = (from: number, to: number) => {
        const next = [...sections];
        const [moved] = next.splice(from, 1);
        next.splice(to, 0, moved);
        setExpandedIndexes((prev) => {
            const updated = new Set<number>();
            prev.forEach((idx) => {
                if (idx === from) {
                    updated.add(to);
                } else if (from < to && idx > from && idx <= to) {
                    updated.add(idx - 1);
                } else if (from > to && idx >= to && idx < from) {
                    updated.add(idx + 1);
                } else {
                    updated.add(idx);
                }
            });
            return updated;
        });
        onChange(next);
    };

    const handleLabelChange = (index: number, newLabel: string) => {
        onChange(sections.map((s, i) => (i === index ? { ...s, label: newLabel } : s)));
    };

    const handleBodyChange = (index: number, body: string) => {
        onChange(sections.map((s, i) => (i === index ? { ...s, body } : s)));
    };

    const handleDeleteConfirmed = () => {
        if (pendingDeleteIndex === null) return;
        const index = pendingDeleteIndex;
        setPendingDeleteIndex(null);
        setExpandedIndexes((prev) => {
            const updated = new Set<number>();
            prev.forEach((idx) => {
                if (idx < index) updated.add(idx);
                else if (idx > index) updated.add(idx - 1);
            });
            return updated;
        });
        onChange(sections.filter((_, i) => i !== index));
    };

    const handleAdd = () => {
        const newIndex = sections.length;
        setExpandedIndexes((prev) => new Set([...prev, newIndex]));
        onChange([...sections, { label: t('form.instructionsNewSection'), body: '' }]);
    };

    return (
        <div className="flex flex-col gap-2">
            <SectionDragLayer sections={sections} />
            <div className={classnames('flex justify-between gap-4', helpText ? 'items-start' : 'items-center')}>
                <div className="flex min-w-0 flex-col gap-0.5">
                    <span className="text-sm text-content-medium">
                        <span className="font-normal text-content-medium">{label}</span>
                        {' · '}
                        {t('form.instructionsSectionCount', { count: sections.length })}
                    </span>
                    {helpText && <span className="text-xs text-content-medium">{helpText}</span>}
                </div>
                {sections.length > 0 && (
                    <button
                        type="button"
                        onClick={toggleExpandAll}
                        className="text-sm cursor-pointer text-content-medium hover:text-content-strong flex items-center gap-1.5 transition-colors"
                    >
                        <DoubleArrowDownIcon
                            className={classnames('text-xs transition-transform', allExpanded && 'rotate-180')}
                        />
                        {allExpanded ? t('form.instructionsCollapseAll') : t('form.instructionsExpandAll')}
                    </button>
                )}
            </div>
            <div className="flex flex-col gap-2">
                {sections.map((section, index) => (
                    <SectionRow
                        key={index}
                        section={section}
                        index={index}
                        isExpanded={expandedIndexes.has(index)}
                        onToggle={() => toggleSection(index)}
                        onLabelChange={(newLabel) => handleLabelChange(index, newLabel)}
                        onBodyChange={(body) => handleBodyChange(index, body)}
                        onDeleteRequest={() => setPendingDeleteIndex(index)}
                        onMove={handleMove}
                    />
                ))}
            </div>
            <button
                type="button"
                onClick={handleAdd}
                className="flex items-center gap-2 text-sm justify-center w-full border border-dashed cursor-pointer border-line-weak rounded rounded-lg hover:bg-surface-brand-weak p-2 transition-colors text-content-strong    "
            >
                <AddIcon className="text-xs text-content-strong" />
                {t('form.instructionsAddSection')}
            </button>
            {pendingDeleteIndex !== null && (
                <ConfirmModal
                    visible
                    title={t('form.instructionsDeleteSectionTitle')}
                    danger
                    onConfirm={handleDeleteConfirmed}
                    onCancel={() => setPendingDeleteIndex(null)}
                    labelConfirm={t('form.instructionsDeleteSectionConfirm')}
                >
                    {t('form.instructionsDeleteSectionMessage', {
                        label: sections[pendingDeleteIndex]?.label || t('form.instructionsNewSection'),
                    })}
                </ConfirmModal>
            )}
        </div>
    );
};

const InstructionSections = (props: InstructionSectionsProps) => (
    <DndProvider backend={HTML5Backend}>
        <InstructionSectionsInner {...props} />
    </DndProvider>
);

export default InstructionSections;
